import { Helmet } from 'react-helmet';
import { localizePath, stripLanguagePrefix } from '@/utils/languageRoutes';

interface SEOMetaTagsProps {
  language: string;
  path: string;
  pageType?: 'homepage' | 'about' | 'contact' | 'blog' | 'article' | 'service';
  article?: {
    title: string;
    description: string;
    publishedTime: string;
    modifiedTime: string;
    author: string;
    tags: string[];
    thumbnailUrl?: string;
    readingTime?: number;
    canonicalPaths?: {
      en: string;
      ko: string;
      xDefault?: string;
    };
  };
  service?: {
    name: string;
    description: string;
    provider: string;
    areaServed: string;
  };
}

const SEOMetaTags = ({ language, path, pageType = 'homepage', article, service }: SEOMetaTagsProps) => {
  const isKorean = language === 'KO';
  const baseUrl = 'https://durucompliance.com';
  const pathWithoutLanguage = stripLanguagePrefix(path);
  const toAbsoluteUrl = (value: string) => value.startsWith('http') ? value : `${baseUrl}${value.startsWith('/') ? value : `/${value}`}`;
  const englishUrl = article?.canonicalPaths?.en
    ? toAbsoluteUrl(article.canonicalPaths.en)
    : `${baseUrl}${localizePath(pathWithoutLanguage, 'EN')}`;
  const koreanUrl = article?.canonicalPaths?.ko
    ? toAbsoluteUrl(article.canonicalPaths.ko)
    : `${baseUrl}${localizePath(pathWithoutLanguage, 'KO')}`;
  const currentUrl = article?.canonicalPaths
    ? toAbsoluteUrl(isKorean ? article.canonicalPaths.ko : article.canonicalPaths.en)
    : `${baseUrl}${localizePath(pathWithoutLanguage, language)}`;
  const xDefaultUrl = article?.canonicalPaths?.xDefault ? toAbsoluteUrl(article.canonicalPaths.xDefault) : englishUrl;

  const metaTags = {
    EN: {
      homepage: {
        title: 'Duru Compliance | India Market Entry & Business Setup One-Stop Total Solution',
        description:
          'Duru Compliance provides one-stop total solutions for India market entry including company incorporation, business setup, recruitment, and annual accounting services for global companies.',
        keywords:
          'Duru Compliance, India market entry, India business setup, company incorporation India, one-stop total solution, business setup, recruitment services, annual accounting, India expansion experts, India business',
        ogTitle: 'One-Stop Total Solution for India Market Entry | Duru Compliance',
        ogDescription:
          'Company incorporation, business setup, recruitment, and annual accounting services for global companies entering the Indian market.',
        twitterTitle: 'One-Stop Total Solution for India Market Entry | Duru Compliance',
        twitterDescription: 'Company incorporation, business setup, recruitment & accounting for India market entry.',
        locale: 'en_US',
        siteName: 'Duru Compliance',
      },
      about: {
        title: 'About Duru Compliance | Expert India Business Consultants Since 2020',
        description:
          "Meet the Duru Compliance team led by CEO Yongjoo Yoo. We're India business setup experts with 50+ professionals helping global companies with incorporation, compliance, and market expansion since 2020.",
        keywords:
          'about Duru Compliance, India business consultants, company history, our team, our mission, business setup experts India, compliance specialists, Yongjoo Yoo',
        ogTitle: 'About Duru Compliance | Your Trusted India Business Partner',
        ogDescription:
          'Expert team of 50+ professionals dedicated to helping global companies succeed in India. Learn about our mission, values, and the people behind Duru Compliance.',
        twitterTitle: 'About Duru Compliance | India Business Experts',
        twitterDescription: 'Meet the team helping global companies expand to India since 2020.',
        locale: 'en_US',
        siteName: 'Duru Compliance',
      },
      contact: {
        title: 'Contact Duru Compliance | Get Expert India Business Consultation',
        description:
          'Contact Duru Compliance for India business setup, incorporation, and compliance services. Call +91-900-817-7740, email info@duruper.com, or visit our Bengaluru office. Free consultation available.',
        keywords:
          'contact Duru Compliance, India business consultation, Bengaluru office, business setup inquiry, compliance services contact, India business experts',
        ogTitle: 'Contact Duru Compliance | India Business Experts',
        ogDescription:
          'Get in touch with our India business setup experts. Phone: +91-900-817-7740, Email: info@duruper.com. Office in Bengaluru, Karnataka.',
        twitterTitle: 'Contact Duru Compliance',
        twitterDescription: "Reach out for expert India business consultation. We're here to help.",
        locale: 'en_US',
        siteName: 'Duru Compliance',
      },
      blog: {
        title: 'India Business Insights & Compliance Updates | Duru Compliance Blog',
        description:
          'Expert insights on India business setup, compliance, taxation, and market entry. Stay updated with the latest regulations, tips, and success stories from Duru Compliance.',
        keywords:
          'India business insights, compliance updates, taxation news India, business setup tips, market entry guide, India regulations, business blog, India business news',
        ogTitle: 'India Business Insights | Expert Compliance & Setup Guides',
        ogDescription:
          'Latest insights on India business setup, compliance, and market expansion from industry experts.',
        twitterTitle: 'India Business Insights | Duru Compliance',
        twitterDescription: 'Expert guides and updates on India business compliance.',
        locale: 'en_US',
        siteName: 'Duru Compliance',
      },
    },
    KO: {
      homepage: {
        title: '두루컴플라이언스 | 인도 시장 진출·인도 법인 설립 원스탑 토탈 솔루션 | Duru Compliance',
        description:
          '두루컴플라이언스는 인도 시장 진출을 위한 원스탑 토탈 솔루션으로 법인 설립, 사업 셋업, 인력 채용, 연간 회계 업무까지 한국 기업의 인도 진출을 종합적으로 지원합니다.',
        keywords:
          '두루컴플라이언스, 두루 컴플라이언스, Duru Compliance, 인도 진출, 인도 시장 진출, 인도 법인 설립, 원스탑 토탈 솔루션, 사업 셋업, 인력 채용, 연간 회계 업무, 인도 진출 전문, 인도 비즈니스',
        ogTitle: '인도 시장 진출을 위한 원스탑 토탈 솔루션 | 두루컴플라이언스',
        ogDescription:
          '법인 설립, 사업 셋업, 인력 채용, 연간 회계 업무까지 한국 기업의 인도 시장 진출을 종합적으로 지원합니다.',
        twitterTitle: '인도 시장 진출을 위한 원스탑 토탈 솔루션 | 두루컴플라이언스',
        twitterDescription: '법인 설립, 사업 셋업, 인력 채용, 연간 회계 업무까지 한국 기업의 인도 진출을 종합 지원',
        locale: 'ko_KR',
        siteName: '두루컴플라이언스',
      },
      about: {
        title: '두루컴플라이언스 소개 | 인도 비즈니스 전문가 팀',
        description:
          '유용주 대표가 이끄는 두루컴플라이언스 팀을 만나보세요. 2020년부터 50명 이상의 전문가가 글로벌 기업의 인도 시장 진출을 돕고 있습니다.',
        keywords:
          '두루컴플라이언스 소개, 두루 컴플라이언스 소개, 인도 비즈니스 컨설턴트, 회사 소개, 팀 소개, 미션, 인도 법인 설립 전문가, 유용주',
        ogTitle: '두루컴플라이언스 소개 | 신뢰할 수 있는 인도 비즈니스 파트너',
        ogDescription:
          '글로벌 기업의 인도 성공을 돕는 50명 이상의 전문가 팀. 두루컴플라이언스의 미션과 가치, 그리고 팀을 소개합니다.',
        twitterTitle: '두루컴플라이언스 소개 | 인도 비즈니스 전문가',
        twitterDescription: '2020년부터 글로벌 기업의 인도 진출을 돕는 전문가 팀을 만나보세요.',
        locale: 'ko_KR',
        siteName: '두루컴플라이언스',
      },
      contact: {
        title: '두루컴플라이언스 연락처 | 인도 비즈니스 상담',
        description:
          '인도 법인 설립 및 컴플라이언스 서비스 문의는 두루컴플라이언스로. 전화: +91-900-817-7740, 이메일: info@duruper.com. 벵갈루루 사무실 방문 가능. 무료 상담 제공.',
        keywords:
          '두루컴플라이언스 연락처, 두루 컴플라이언스 연락처, 인도 비즈니스 상담, 벵갈루루 사무실, 법인 설립 문의, 인도 비즈니스 전문가',
        ogTitle: '두루컴플라이언스 연락처 | 인도 비즈니스 전문가',
        ogDescription:
          '인도 비즈니스 전문가에게 문의하세요. 전화: +91-900-817-7740, 이메일: info@duruper.com. 벵갈루루 사무실 위치 안내.',
        twitterTitle: '두루컴플라이언스 연락처',
        twitterDescription: '인도 비즈니스 전문 상담을 받아보세요. 무료 상담 가능합니다.',
        locale: 'ko_KR',
        siteName: '두루컴플라이언스',
      },
      blog: {
        title: '인도 비즈니스 인사이트 및 컴플라이언스 업데이트 | 두루컴플라이언스 블로그',
        description:
          '인도 법인 설립, 컴플라이언스, 세무 및 시장 진출에 대한 전문가 인사이트. 최신 규정, 팁, 성공 사례를 확인하세요.',
        keywords:
          '인도 비즈니스 인사이트, 컴플라이언스 업데이트, 인도 세무 뉴스, 법인 설립 팁, 시장 진출 가이드, 인도 규정, 비즈니스 블로그',
        ogTitle: '인도 비즈니스 인사이트 | 전문가 컴플라이언스 가이드',
        ogDescription: '업계 전문가의 인도 비즈니스 설립, 컴플라이언스 및 시장 확장에 대한 최신 인사이트.',
        twitterTitle: '인도 비즈니스 인사이트 | 두루컴플라이언스',
        twitterDescription: '인도 비즈니스 컴플라이언스에 대한 전문가 가이드 및 업데이트.',
        locale: 'ko_KR',
        siteName: '두루컴플라이언스',
      },
    },
  };

  // For articles, use dynamic data; for other pages, use static meta tags
  const content =
    pageType === 'article' && article
      ? {
          title: `${article.title} | ${isKorean ? '두루컴플라이언스' : 'Duru Compliance'}`,
          description: article.description,
          keywords: article.tags.join(', '),
          ogTitle: article.title,
          ogDescription: article.description,
          twitterTitle: article.title,
          twitterDescription: article.description,
          locale: isKorean ? 'ko_KR' : 'en_US',
          siteName: isKorean ? '두루컴플라이언스' : 'Duru Compliance',
        }
      : metaTags[isKorean ? 'KO' : 'EN'][pageType];

  // Generate appropriate schema based on page type
  const getSchema = () => {
    const baseSchema = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: isKorean ? '두루컴플라이언스' : 'Duru Compliance',
      url: baseUrl,
      logo: `${baseUrl}/og-image.png`,
      sameAs: [
        'https://www.linkedin.com/company/duru-compliance/',
        'https://www.facebook.com/durucompliance',
        'https://www.instagram.com/duru_compliance/',
      ],
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+91-900-817-7740',
        contactType: 'customer service',
        availableLanguage: ['English', 'Korean'],
      },
    };

    if (pageType === 'article' && article) {
      return {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: article.title,
        description: article.description,
        url: currentUrl,
        image: article.thumbnailUrl || `${baseUrl}/og-image.png`,
        author: {
          '@type': 'Person',
          name: article.author,
        },
        datePublished: article.publishedTime,
        dateModified: article.modifiedTime || article.publishedTime,
        publisher: {
          '@type': 'Organization',
          '@id': `${baseUrl}/#organization`,
          name: isKorean ? '두루컴플라이언스' : 'Duru Compliance',
          url: baseUrl,
          logo: {
            '@type': 'ImageObject',
            url: `${baseUrl}/og-image.png`,
          },
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': currentUrl,
        },
        isPartOf: {
          '@type': 'Blog',
          name: isKorean ? '두루컴플라이언스 인사이트' : 'Duru Compliance Insights',
          url: isKorean ? `${baseUrl}/ko/insights` : `${baseUrl}/en/insights`,
        },
        keywords: article.tags.join(', '),
        articleSection: 'Business Compliance',
        inLanguage: isKorean ? 'ko' : 'en',
        wordCount: article.readingTime ? article.readingTime * 200 : undefined,
        timeRequired: article.readingTime ? `PT${article.readingTime}M` : undefined,
      };
    }

    if (pageType === 'service' && service) {
      return {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: service.name,
        description: service.description,
        provider: {
          '@type': 'Organization',
          name: service.provider,
        },
        areaServed: service.areaServed,
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': currentUrl,
        },
      };
    }

    if (pageType === 'about') {
      return {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: content.title,
        description: content.description,
        url: currentUrl,
        mainEntity: {
          '@type': 'Organization',
          name: isKorean ? '두루컴플라이언스' : 'Duru Compliance',
          alternateName: isKorean ? 'Duru Compliance' : '두루컴플라이언스',
          url: baseUrl,
          logo: `${baseUrl}/og-image.png`,
          foundingDate: '2020-07-15',
          founder: {
            '@type': 'Person',
            name: 'Yongjoo Yoo',
            jobTitle: 'CEO & Founder',
            nationality: 'South Korea',
          },
          numberOfEmployees: {
            '@type': 'QuantitativeValue',
            value: 50,
          },
          slogan: isKorean
            ? '글로벌 기업의 인도 시장 진출을 위한 원스톱 솔루션'
            : 'One-stop solutions for global companies entering the Indian market',
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'No. 23, 1st Main Rd, Jakkur Layout, Jakkur Village, MCHS, Jakkuru',
            addressLocality: 'Bengaluru',
            addressRegion: 'Karnataka',
            postalCode: '560064',
            addressCountry: 'IN',
          },
          contactPoint: {
            '@type': 'ContactPoint',
            telephone: '+91-900-817-7740',
            email: 'info@duruper.com',
            contactType: 'customer service',
            availableLanguage: ['English', 'Korean'],
          },
          sameAs: [
            'https://www.linkedin.com/company/duru-compliance/',
            'https://www.facebook.com/durucompliance',
            'https://www.instagram.com/duru_compliance/',
          ],
        },
      };
    }

    if (pageType === 'contact') {
      return {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        name: content.title,
        description: content.description,
        url: currentUrl,
        mainEntity: {
          '@type': ['Organization', 'LocalBusiness'],
          name: isKorean ? '두루컴플라이언스' : 'Duru Compliance',
          alternateName: isKorean ? 'Duru Compliance' : '두루컴플라이언스',
          url: baseUrl,
          logo: `${baseUrl}/og-image.png`,
          telephone: '+91-900-817-7740',
          email: 'info@duruper.com',
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'No. 23, 1st Main Rd, Jakkur Layout, Jakkur Village, MCHS, Jakkuru',
            addressLocality: 'Bengaluru',
            addressRegion: 'Karnataka',
            postalCode: '560064',
            addressCountry: 'IN',
          },
          geo: {
            '@type': 'GeoCoordinates',
            latitude: '13.0827',
            longitude: '77.5877',
          },
          openingHours: 'Mo-Fr 09:00-18:00',
          contactPoint: [
            {
              '@type': 'ContactPoint',
              telephone: '+91-900-817-7740',
              email: 'info@duruper.com',
              contactType: 'customer service',
              availableLanguage: ['English', 'Korean'],
              areaServed: 'IN',
            },
            {
              '@type': 'ContactPoint',
              telephone: '+91-900-817-7740',
              contactType: 'sales',
              availableLanguage: ['English', 'Korean'],
              areaServed: ['IN', 'KR'],
            },
          ],
          sameAs: [
            'https://www.linkedin.com/company/duru-compliance/',
            'https://www.facebook.com/durucompliance',
            'https://www.instagram.com/duru_compliance/',
          ],
        },
      };
    }

    if (pageType === 'blog') {
      return {
        '@context': 'https://schema.org',
        '@type': 'Blog',
        name: isKorean ? '두루컴플라이언스 블로그' : 'Duru Compliance Blog',
        description: content.description,
        url: currentUrl,
        publisher: {
          '@type': 'Organization',
          name: isKorean ? '두루컴플라이언스' : 'Duru Compliance',
          url: baseUrl,
          logo: {
            '@type': 'ImageObject',
            url: `${baseUrl}/og-image.png`,
          },
          sameAs: [
            'https://www.linkedin.com/company/duru-compliance/',
            'https://www.facebook.com/durucompliance',
            'https://www.instagram.com/duru_compliance/',
          ],
        },
        inLanguage: isKorean ? 'ko' : 'en',
        about: {
          '@type': 'Thing',
          name: isKorean ? '인도 비즈니스 및 컴플라이언스' : 'India Business and Compliance',
          description: isKorean
            ? '인도 시장 진출, 법인 설립, 컴플라이언스 및 세무에 대한 전문 정보'
            : 'Expert information on India market entry, company incorporation, compliance, and taxation',
        },
      };
    }

    return baseSchema;
  };

  const getBreadcrumbSchema = () => {
    const homeUrl = isKorean ? `${baseUrl}/ko` : `${baseUrl}/en`;
    const items = [{ name: isKorean ? '홈' : 'Home', url: homeUrl }];

    if (pageType === 'article' && article) {
      items.push({ name: isKorean ? '인사이트' : 'Insights', url: isKorean ? `${baseUrl}/ko/insights` : `${baseUrl}/en/insights` });
      items.push({ name: article.title, url: currentUrl });
    } else if (pageType === 'blog') {
      items.push({ name: isKorean ? '인사이트' : 'Insights', url: currentUrl });
    } else if (pageType === 'about') {
      items.push({ name: isKorean ? '회사 소개' : 'About', url: currentUrl });
    } else if (pageType === 'contact') {
      items.push({ name: isKorean ? '문의' : 'Contact', url: currentUrl });
    }

    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: item.url,
      })),
    };
  };

  return (
    <Helmet htmlAttributes={{ lang: isKorean ? 'ko' : 'en' }}>
      <title>{content.title}</title>
      <meta name="description" content={content.description} />
      <meta name="keywords" content={content.keywords} />
      {pageType === 'article' && article && <meta name="author" content={article.author} />}

      {/* OpenGraph Tags */}
      <meta property="og:title" content={content.ogTitle} />
      <meta property="og:description" content={content.ogDescription} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:type" content={pageType === 'article' ? 'article' : 'website'} />
      <meta
        property="og:image"
        content={pageType === 'article' && article?.thumbnailUrl ? article.thumbnailUrl : `${baseUrl}/og-image.png`}
      />
      <meta property="og:site_name" content={content.siteName} />
      <meta property="og:locale" content={content.locale} />
      <meta property="og:locale:alternate" content={isKorean ? 'en_US' : 'ko_KR'} />

      {/* Article-specific tags */}
      {pageType === 'article' && article && <meta property="article:published_time" content={article.publishedTime} />}
      {pageType === 'article' && article && <meta property="article:modified_time" content={article.modifiedTime} />}
      {pageType === 'article' && article && <meta property="article:author" content={article.author} />}
      {pageType === 'article' &&
        article &&
        article.tags.map((tag) => <meta property="article:tag" content={tag} key={tag} />)}

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={content.twitterTitle} />
      <meta name="twitter:description" content={content.twitterDescription} />
      <meta
        name="twitter:image"
        content={pageType === 'article' && article?.thumbnailUrl ? article.thumbnailUrl : `${baseUrl}/og-image.png`}
      />
      <meta name="twitter:site" content={content.siteName} />

      {/* Canonical and Language Alternates */}
      <link rel="canonical" href={currentUrl} />
      <link rel="alternate" hrefLang="en" href={englishUrl} />
      <link rel="alternate" hrefLang="ko" href={koreanUrl} />
      <link rel="alternate" hrefLang="x-default" href={xDefaultUrl} />

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">{JSON.stringify(getSchema())}</script>
      <script type="application/ld+json">{JSON.stringify(getBreadcrumbSchema())}</script>
    </Helmet>
  );
};

export default SEOMetaTags;
